import React, { useState, useEffect } from 'react';
import BossDialog from "../../components/BossDialog";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { EditPageHeader } from "../../components/EditPageHeader";
import { useTranslation } from "../../locale/useTranslation";
import { BOOLEAN_STATUS, SUBTITLING_CODE, ENTITYNAME, KEY_ASSET_GROUP_TYPE, SUBTITLING_MODE_TYPE, TOAST_TYPES } from "../constant/constant";
import { utility } from "../utility/utilityProvider";
import * as API from "../API/api";
import { Form, FormElement } from "@progress/kendo-react-form";
import moment from 'moment';
import ToastNotification from '../../components/ToastNotification';

/**
 * Unified dialog for selecting / editing an Ingest or Transcode prototype.
 *
 * @param {"ingest"|"transcode"} mode – controls fields, title & submit label.
 * @param {Function}  onClose        – called when the dialog should close.
 * @param {Function}  onSubmit       – receives the full prototype data-item.
 *                                     In ingest mode: { ingestPrototype, transcodePrototype }
 * @param {Array}     data           – list of prototypes to pick from.
 * @param {Function}  onChange       – setter for the selected dropdown value.
 * @param {Object}    value          – currently selected prototype in the dropdown.
 * @param {Object}    [initialData]  – optional pre-populated prototype (edit case).
 * @param {Array}     [transcodePrototypesData] – transcode prototypes list (ingest mode only).
 * @param {Object}    [initialTranscodeData]    – pre-populated transcode prototype (ingest mode only).
 */
const PrototypeDialog = ({
    mode = "ingest",
    onClose,
    onSubmit,
    data,
    value,
    onChange,
    initialData,
    transcodePrototypesData = [],
    initialTranscodeData = null,
}) => {
    const lang = useTranslation();
    const [transmissionTypes, setTransmissionTypes] = useState([]);
    const [transcodeDefinitions, setTranscodeDefinitions] = useState([]);
    const [dataItem, setDataItem] = useState({});
    const [initialPopulated, setInitialPopulated] = useState(false);

    // Transcode tab state (only used in ingest mode)
    const [transcodeDataItem, setTranscodeDataItem] = useState({});
    const [transcodeValue, setTranscodeValue] = useState({});
    const [transcodeInitialPopulated, setTranscodeInitialPopulated] = useState(false);

    // Tab state (only relevant in ingest mode)
    const [activeTab, setActiveTab] = useState("ingest");

    const isIngest = mode === "ingest";
    const dialogTitle = isIngest ? lang.ingest_Work_order_label : lang.select_transcode_prototype;
    const submitLabel = isIngest ? lang.publish_button : lang.submit_button;

    const booleans = Object.keys(BOOLEAN_STATUS).map((obj) => ({ _id: BOOLEAN_STATUS[obj], Description: obj }));
    const assetGroups = Object.keys(KEY_ASSET_GROUP_TYPE).map((obj) => ({ _id: KEY_ASSET_GROUP_TYPE[obj], Description: obj }));
    const subtitlingModes = Object.keys(SUBTITLING_MODE_TYPE).map((obj) => ({ _id: SUBTITLING_MODE_TYPE[obj], Description: obj }));

    useEffect(() => {
        loadCombo();
    }, []);

    const loadCombo = async () => {
        let transmissionTypeRes = await API.getDataLookup(ENTITYNAME.TransmissionTypes, { sort: { SID: 1 } });
        setTransmissionTypes(transmissionTypeRes.data);

        // Load transcode definitions for transcode mode OR for ingest mode's transcode tab
        const transcodeDefinitionsRes = await API.getDataLookup(ENTITYNAME.TranscodeDefinition, { sort: { Description: 1 } });
        setTranscodeDefinitions(transcodeDefinitionsRes.data);
    };

    // Helper to get a raw number from a value that might be an object { _id: x } or a plain number
    const rawVal = (v) => (v !== null && typeof v === 'object' && v._id !== undefined) ? v._id : v;

    // Helper: resolve TransmissionType whether it's a raw ID or a full object
    // Always returns the actual object from ttList for Kendo DropDownList reference matching
    const resolveTransmissionType = (tt, ttList) => {
        if (!tt) return {};
        const lookupId = (typeof tt === 'object' && tt._id !== undefined) ? tt._id : tt;
        const found = ttList.find(t => t._id === lookupId);
        if (found) return found;
        // Fallback: if not found in list, return a displayable object
        if (typeof tt === 'object' && tt._id !== undefined) {
            return { _id: tt._id, Name: tt.Name };
        }
        return {};
    };

    // ── Build ingest data item from a selected prototype ──
    const buildIngestDataItem = (selectedValue, ttList) => ({
        _id: selectedValue._id,
        SID: selectedValue.SID,
        Description: selectedValue.Description ?? "",
        TransmissionType: resolveTransmissionType(selectedValue.TransmissionType, ttList),
        DirectIngest: { _id: rawVal(selectedValue.DirectIngest), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.DirectIngest)) },
        CensorshipFlag: { _id: rawVal(selectedValue.CensorshipFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.CensorshipFlag)) },
        AssetGroup: { _id: rawVal(selectedValue.AssetGroup), Description: utility.getKeyByValue(KEY_ASSET_GROUP_TYPE, rawVal(selectedValue.AssetGroup)) },
        DualSound: { _id: rawVal(selectedValue.DualSound), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.DualSound)) },
        SubtitlesFlag: { _id: rawVal(selectedValue.SubtitlesFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.SubtitlesFlag)) },
        SubtitlingCode: { _id: rawVal(selectedValue.SubtitlingCode), Description: utility.getKeyByValue(SUBTITLING_CODE, rawVal(selectedValue.SubtitlingCode)) },
        SubtitlingLang: selectedValue.SubtitlingLang ?? "",
        SubtitlingMode: { _id: rawVal(selectedValue.SubtitlingMode), Description: utility.getKeyByValue(SUBTITLING_MODE_TYPE, rawVal(selectedValue.SubtitlingMode)) },
        Thumbnail: { _id: rawVal(selectedValue.Thumbnail), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.Thumbnail)) },
        Language1: selectedValue.Language1 ?? "",
        HD: { _id: rawVal(selectedValue.HD), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.HD)) },
        IsDefault: selectedValue.IsDefault ?? false,
        Language2: selectedValue.Language2 ?? "",
        SecondaryCopy: { _id: rawVal(selectedValue.SecondaryCopy), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.SecondaryCopy)) },
        Archive: selectedValue.Archive ?? false,
    });

    // ── Build transcode data item from a selected prototype ──
    const buildTranscodeDataItem = (selectedValue, ttList) => ({
        _id: selectedValue._id,
        SID: selectedValue.SID,
        Description: selectedValue.Description ?? "",
        TransmissionType: resolveTransmissionType(selectedValue.TransmissionType, ttList),
        TitleFlag: { _id: rawVal(selectedValue.TitleFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.TitleFlag)) },
        LogoFlag: { _id: rawVal(selectedValue.LogoFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.LogoFlag)) },
        LogoOpposite: { _id: rawVal(selectedValue.LogoOpposite), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.LogoOpposite)) },
        EndingFlag: { _id: rawVal(selectedValue.EndingFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.EndingFlag)) },
        Definition: (selectedValue.Definition && Object.keys(selectedValue.Definition).length > 0) ? selectedValue.Definition : {},
        ReverseAudioChannelFlag: { _id: rawVal(selectedValue.ReverseAudioChannelFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.ReverseAudioChannelFlag)) },
        TagFlag: { _id: rawVal(selectedValue.TagFlag), Description: utility.getKeyByValue(BOOLEAN_STATUS, rawVal(selectedValue.TagFlag)) },
        AudioMode: selectedValue.AudioMode ?? "",
        IsDefault: selectedValue.IsDefault ?? false,
        Archive: selectedValue.Archive ?? false,
    });

    // ── Fill values for primary (ingest or transcode depending on mode) ──
    // Called from the fill button click — always uses latest state
    const handleFillValues = (overrideValue) => {
        let selectedValue = overrideValue || value;
        if (typeof selectedValue === 'string') {
            selectedValue = data?.find(item => item._id === selectedValue);
        }

        if (selectedValue) {
            if (isIngest) {
                setDataItem(buildIngestDataItem(selectedValue, transmissionTypes));
            } else {
                setDataItem(buildTranscodeDataItem(selectedValue, transmissionTypes));
            }
        } else {
            setDataItem({});
        }
    };

    // ── Fill values for the transcode tab (ingest mode only) ──
    const handleFillTranscodeValues = (overrideValue) => {
        let selectedValue = overrideValue || transcodeValue;
        if (typeof selectedValue === 'string') {
            selectedValue = transcodePrototypesData?.find(item => item._id === selectedValue);
        }
        if (selectedValue) {
            setTranscodeDataItem(buildTranscodeDataItem(selectedValue, transmissionTypes));
        } else {
            setTranscodeDataItem({});
        }
    };

    // ── Populate initial ingest data (wait for transmissionTypes to load) ──
    useEffect(() => {
        if (!initialPopulated && transmissionTypes.length > 0) {
            if (initialData && Object.keys(initialData).length > 0) {
                if (isIngest) {
                    setDataItem(buildIngestDataItem(initialData, transmissionTypes));
                } else {
                    setDataItem(buildTranscodeDataItem(initialData, transmissionTypes));
                }
                setInitialPopulated(true);
            } else if (data && data.length > 0) {
                setInitialPopulated(true);
            }
        }
    }, [initialData, data, initialPopulated, transmissionTypes, isIngest]);

    // ── Populate initial transcode data (ingest mode only, wait for transmissionTypes) ──
    useEffect(() => {
        if (isIngest && !transcodeInitialPopulated && transmissionTypes.length > 0) {
            if (initialTranscodeData && Object.keys(initialTranscodeData).length > 0) {
                setTranscodeDataItem(buildTranscodeDataItem(initialTranscodeData, transmissionTypes));
                setTranscodeInitialPopulated(true);
            } else if (transcodePrototypesData && transcodePrototypesData.length > 0) {
                setTranscodeValue(transcodePrototypesData[0]);
                setTranscodeInitialPopulated(true);
            }
        }
    }, [isIngest, initialTranscodeData, transcodePrototypesData, transcodeInitialPopulated, transmissionTypes]);

    const onChangeForm = (e) => {
        if (e.target.name === "Archive") {
            setDataItem({ ...dataItem, Archive: !dataItem.Archive });
        } else if (e.target.name === "IsDefault") {
            setDataItem({ ...dataItem, IsDefault: !dataItem.IsDefault });
        } else {
            setDataItem({ ...dataItem, [e.target.name]: e.target.value });
        }
    };

    const onChangeTranscodeForm = (e) => {
        if (e.target.name === "Archive") {
            setTranscodeDataItem({ ...transcodeDataItem, Archive: !transcodeDataItem.Archive });
        } else if (e.target.name === "IsDefault") {
            setTranscodeDataItem({ ...transcodeDataItem, IsDefault: !transcodeDataItem.IsDefault });
        } else {
            setTranscodeDataItem({ ...transcodeDataItem, [e.target.name]: e.target.value });
        }
    };

    // ── Flatten dropdown objects back to raw _id ──
    const flattenData = (item) => {
        const flattened = { ...item };
        for (const key of Object.keys(flattened)) {
            const val = flattened[key];
            // Keep TransmissionType and Definition as full objects for reload display
            if (key === 'Definition' || key === 'TransmissionType' || key === 'SubtitlingCode') continue;
            if (key === 'ExpectedDate') {
                flattened[key] = moment.utc(val).valueOf();
            };
            if (val !== null && typeof val === 'object' && !Array.isArray(val) && val._id !== undefined) {
                flattened[key] = val._id;
            }
        }
        return flattened;
    };

    // Helper to check if prototype has been attached (form populated)
    const isFormValid = (item) => {
        if (!item || Object.keys(item).length === 0) return false;
        if (!item.Description || item.Description.trim() === "") return false;
        return true;
    };

    const handleSubmit = () => {
        if (isIngest) {
            // Validate ingest prototype is attached
            if (!isFormValid(dataItem)) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_attach_ingest_prototype_error_message });
                setActiveTab("ingest");
                return;
            }
            if (!dataItem.ExpectedDate) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_expected_date_error_message });
                // setActiveTab("ingest");
                return;
            }
            // Validate transcode prototype is attached
            if (!isFormValid(transcodeDataItem)) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_attach_transcode_prototype_error_message });
                setActiveTab("transcode");
                return;
            }
            // Submit both prototypes
            onSubmit({
                ingestPrototype: flattenData(dataItem),
                transcodePrototype: flattenData(transcodeDataItem),
            });
        } else {
            // Validate transcode prototype is attached
            if (!isFormValid(dataItem)) {
                ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_attach_transcode_prototype_error_message });
                return;
            }
            onSubmit(flattenData(dataItem));
        }
    };

    const isIngestPrototypeValid = dataItem?.Description?.trim() && dataItem?.AssetGroup?._id && dataItem?.DirectIngest?._id;
    const isTranscodePrototypeValid = transcodeDataItem?.Description?.trim() && transcodeDataItem?.TransmissionType?._id;

    const sectionLabel = { fontSize: "10px", fontWeight: "600", color: "#999", textTransform: "uppercase", letterSpacing: "0.8px", margin: "0" };
    const fieldLabel = { fontSize: "12px", color: "#555", marginBottom: "2px" };

    const tabStyle = (tabName) => ({
        padding: "8px 20px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: activeTab === tabName ? "600" : "400",
        color: activeTab === tabName ? "#0d6efd" : "#666",
        borderBottom: activeTab === tabName ? "2px solid #0d6efd" : "2px solid transparent",
        background: "none",
        border: "none",
        borderBottomWidth: "2px",
        borderBottomStyle: "solid",
        borderBottomColor: activeTab === tabName ? "#0d6efd" : "transparent",
        transition: "all 0.2s ease",
    });

    // ── Render the quick-fill toolbar ──
    const renderQuickFill = (ddData, ddValue, ddOnChange, fillFn) => (
        <div className="d-flex align-items-center mb-2" style={{ gap: "6px", padding: "6px 10px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
            <i className="fas fa-wand-magic-sparkles" style={{ fontSize: "11px", color: "#aaa" }}></i>
            <DropDownList
                data={[...ddData].sort((a, b) => a.Description.localeCompare(b.Description))}
                valueField="_id"
                textField="Description"
                value={ddValue}
                onChange={(e) => ddOnChange(e.value)}
                style={{ flex: 1 }}
                size="small"
            />
            <button
                type="button"
                onClick={() => fillFn()}
                title={lang.fill_fields_from_selected_template_tooltip}
                style={{ background: "none", border: "none", color: "#0d6efd", cursor: "pointer", fontSize: "14px", padding: "2px 6px" }}
            >
                <i className="fas fa-arrow-down"></i>
            </button>
        </div>
    );

    // ── Render ingest form fields ──
    const renderIngestFields = () => (
        <Form
            initialValues={dataItem}
            render={() => (
                <FormElement>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.description_label} <span style={{ color: "red" }}> *</span></label>
                            <input name="Description" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.Description || ""} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.transmission_type_label} </label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transmissionTypes} name="TransmissionType" textField="Name" dataItemKey="_id" value={dataItem.TransmissionType || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.asset_group_label} <span style={{ color: "red" }}> *</span></label>
                            <DropDownList style={{ backgroundColor: "white" }} data={assetGroups} name="AssetGroup" textField="Description" dataItemKey="_id" value={dataItem.AssetGroup || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.language1_label}</label>
                            <input name="Language1" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.Language1 || ""} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.language2_label}</label>
                            <input name="Language2" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.Language2 || ""} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>Flags</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.direct_ingest_label} <span style={{ color: "red" }}> *</span></label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="DirectIngest" textField="Description" dataItemKey="_id" value={dataItem.DirectIngest || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.censorship_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="CensorshipFlag" textField="Description" dataItemKey="_id" value={dataItem.CensorshipFlag || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.dual_sound_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="DualSound" textField="Description" dataItemKey="_id" value={dataItem.DualSound || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.thumbnail_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="Thumbnail" textField="Description" dataItemKey="_id" value={dataItem.Thumbnail || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.hd_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="HD" textField="Description" dataItemKey="_id" value={dataItem.HD || {}} onChange={onChangeForm} />
                        </div>
                         <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.secondary_copy_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="SecondaryCopy" textField="Description" dataItemKey="_id" value={dataItem.SecondaryCopy || {}} onChange={onChangeForm} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>{lang.subtitling_label}</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitles_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="SubtitlesFlag" textField="Description" dataItemKey="_id" value={dataItem.SubtitlesFlag || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitling_code_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={Object.keys(SUBTITLING_CODE).map((obj) => ({ _id: SUBTITLING_CODE[obj], Description: obj }))} name="SubtitlingCode" textField="Description" dataItemKey="_id" value={dataItem.SubtitlingCode || {}} onChange={onChangeForm} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitling_lang_label}</label>
                            <input name="SubtitlingLang" type="text" className="form-control form-control-sm" onChange={onChangeForm} value={dataItem.SubtitlingLang || ""} />
                        </div>
                        <div className="col-3 mb-2">
                            <label style={fieldLabel}>{lang.subtitling_mode_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={subtitlingModes} name="SubtitlingMode" textField="Description" dataItemKey="_id" value={dataItem.SubtitlingMode || {}} onChange={onChangeForm} />
                        </div>
                    </div>
                    <div className="d-flex align-items-center mt-1" style={{ gap: "20px" }}>
                        <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                            <input type="checkbox" name="IsDefault" onChange={onChangeForm} checked={dataItem.IsDefault || false} style={{ marginRight: "5px" }} />
                            {lang.set_as_default_label}
                        </label>
                        <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                            <input type="checkbox" name="Archive" onChange={onChangeForm} checked={dataItem.Archive || false} style={{ marginRight: "5px" }} />
                            {lang.archive}
                        </label>
                    </div>
                </FormElement>
            )}
        />
    );

    // ── Render transcode form fields (reused in both modes) ──
    const renderTranscodeFields = (currentDataItem, currentOnChangeForm) => (
        <Form
            initialValues={currentDataItem}
            render={() => (
                <FormElement>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.description_label} <span style={{ color: "red" }}> *</span></label>
                            <input name="Description" type="text" className="form-control form-control-sm" onChange={currentOnChangeForm} value={currentDataItem.Description || ""} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.transmission_type_label} <span style={{ color: "red" }}> *</span></label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transmissionTypes} name="TransmissionType" textField="Name" dataItemKey="_id" value={currentDataItem.TransmissionType || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.audio_mode_label}</label>
                            <input name="AudioMode" type="text" className="form-control form-control-sm" onChange={currentOnChangeForm} value={currentDataItem.AudioMode || ""} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>{lang.flags_label}</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.title_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="TitleFlag" textField="Description" dataItemKey="_id" value={currentDataItem.TitleFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.logo_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="LogoFlag" textField="Description" dataItemKey="_id" value={currentDataItem.LogoFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.logo_opposite_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="LogoOpposite" textField="Description" dataItemKey="_id" value={currentDataItem.LogoOpposite || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.ending_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="EndingFlag" textField="Description" dataItemKey="_id" value={currentDataItem.EndingFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.reverse_audio_channel_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="ReverseAudioChannelFlag" textField="Description" dataItemKey="_id" value={currentDataItem.ReverseAudioChannelFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                        <div className="col-4 mb-2">
                            <label style={fieldLabel}>{lang.tag_flag_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={booleans} name="TagFlag" textField="Description" dataItemKey="_id" value={currentDataItem.TagFlag || {}} onChange={currentOnChangeForm} />
                        </div>
                    </div>
                    <hr style={{ margin: "4px 0 8px", borderColor: "#eee" }} />
                    <p style={sectionLabel}>{lang.definition_label}</p>
                    <div className="row" style={{ rowGap: "0" }}>
                        <div className="col-6 mb-2">
                            <label style={fieldLabel}>{lang.definition_label}</label>
                            <DropDownList style={{ backgroundColor: "white" }} data={transcodeDefinitions} name="Definition" textField="Description" dataItemKey="_id" value={currentDataItem.Definition || {}} onChange={currentOnChangeForm} />
                        </div>
                    </div>
                    <div className="d-flex align-items-center mt-1" style={{ gap: "20px" }}>
                        <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                            <input type="checkbox" name="IsDefault" onChange={currentOnChangeForm} checked={currentDataItem.IsDefault || false} style={{ marginRight: "5px" }} />
                            {lang.set_as_default_label}
                        </label>
                        <label style={{ fontSize: "12px", color: "#555", cursor: "pointer", margin: 0 }}>
                            <input type="checkbox" name="Archive" onChange={currentOnChangeForm} checked={currentDataItem.Archive || false} style={{ marginRight: "5px" }} />
                            {lang.archive}
                        </label>
                    </div>
                </FormElement>
            )}
        />
    );

    return (
        <BossDialog onClose={onClose} title={dialogTitle} width={"620px"}>
            <div className="row align-items-center">
                <div className="col">
                    <EditPageHeader
                        saveButtonTitle={submitLabel}
                        showTitle={false}
                        onCancel={onClose}
                        onSubmit={handleSubmit}
                    />
                </div>
                {isIngest && (
                <div className="col-4">
                    <div className="d-flex flex-column">
                        <label>{lang.expected_date_label} <span style={{ color: "red" }}> *</span></label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            name="ExpectedDate"
                            value={dataItem.ExpectedDate}
                            onChange={onChangeForm}
                        />
                    </div>
                </div>
                )}
            </div>

            {/* ── Tabs (only in ingest mode) ── */}
            {isIngest && (
                <div style={{ display: "flex", borderBottom: "1px solid #dee2e6", marginBottom: "10px" }}>
                    <button type="button" style={tabStyle("ingest")} onClick={() => setActiveTab("ingest")}>
                        <i className="fas fa-indent" style={{ marginRight: "6px" }}></i>{lang.ingest_label}
                        {isIngestPrototypeValid && (
                            <i className="fas fa-check-circle" style={{ marginLeft: "6px", color: "#198754", fontSize: "11px" }}></i>
                        )}
                    </button>
                    <button type="button" style={tabStyle("transcode")} onClick={() => setActiveTab("transcode")}>
                        <i className="fas fa-globe" style={{ marginRight: "6px" }}></i>{lang.transcode_label}
                        {isTranscodePrototypeValid && (
                            <i className="fas fa-check-circle" style={{ marginLeft: "6px", color: "#198754", fontSize: "11px" }}></i>
                        )}
                    </button>
                </div>
            )}

            {/* ── INGEST MODE ── */}
            {isIngest && activeTab === "ingest" && (
                <>
                    {renderQuickFill(data, value, onChange, handleFillValues)}
                    {renderIngestFields()}
                </>
            )}

            {isIngest && activeTab === "transcode" && (
                <>
                    {renderQuickFill(transcodePrototypesData, transcodeValue, setTranscodeValue, handleFillTranscodeValues)}
                    {renderTranscodeFields(transcodeDataItem, onChangeTranscodeForm)}
                </>
            )}

            {/* ── TRANSCODE MODE (no tabs, same as before) ── */}
            {!isIngest && (
                <>
                    {renderQuickFill(data, value, onChange, handleFillValues)}
                    {renderTranscodeFields(dataItem, onChangeForm)}
                </>
            )}
        </BossDialog>
    );
};

export default PrototypeDialog;
